import React, { useState, useEffect, useRef } from "react";
import {
  postCancelSubscription,
  getUserSubscription,
  getSubscriptionDetails,
  postCheckSubscriptionCoupon,
} from "../../core/api/Api";
import { showToast } from "../../core/common-service/CommonService";
import { useTranslation } from "react-i18next";
import "./ManageSubscriptions.css";
import useLangNavigate from "./../common/useLangNavigate";
import Modal from "../common/modal/Modal";
import { showHideModal } from "../../core/common-service/CommonService";
import { getCurrencyForLocale } from "../../config/locales";

const modal_unsubscribe_id = "modal_unsubscribe";

const CANCELLATION_REASONS = [
  "cancel_reason_not_using_enough",
  "cancel_reason_specific_project",
  "cancel_reason_too_expensive",
  "cancel_reason_cheaper_alternative",
  "cancel_reason_not_enough_value",
  "cancel_reason_missing_features",
  "cancel_reason_too_complicated",
  "cancel_reason_technical_issues",
];

const ManageSubscriptions = () => {
  const navigate = useLangNavigate();
  const { t, i18n } = useTranslation();
  const currency = getCurrencyForLocale(i18n.language);
  const [subscription, setSubscription] = useState(null);
  const [status, setStatus] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [showReasonError, setShowReasonError] = useState(false);

  const determineSubscriptionStatus = (data) => {
    if (data.cancel_date && data.cancellation_reason === "payment_failed") {
      return "payment_failed";
    } else if (data.cancel_date) {
      return "unsubscribed";
    } else if (data.cancel_request) {
      return "pending_cancellation";
    } else {
      return "subscribed";
    }
  };

  const onInit = useRef(true);

  useEffect(() => {
    if (!onInit.current) return;
    onInit.current = false;
    const fetchSubscription = async () => {
      const response = await getUserSubscription();
      if (response.data && response.data.id) {
        setSubscription(response.data);
        await getSubscriptionDetails(response.data);
        setStatus(determineSubscriptionStatus(response.data));
      } else {
        setStatus("unsubscribed");
      }
    };

    fetchSubscription();
  }, []);

  const subscribe = () => {
    navigate("/subscriptions", "", { state: { couponCode } });
  };

  const showUnsubscribeModal = () => {
    setSelectedReason("");
    setCustomReason("");
    setIsOtherSelected(false);
    setShowReasonError(false);
    showHideModal(modal_unsubscribe_id);
  };

  const handleReasonSelect = (reasonKey) => {
    setSelectedReason(reasonKey);
    setIsOtherSelected(false);
    setCustomReason("");
    setShowReasonError(false);
  };

  const handleOtherSelect = () => {
    setSelectedReason("");
    setIsOtherSelected(true);
    setShowReasonError(false);
  };

  const confirmUnsubscribe = async () => {
    const reason = isOtherSelected ? customReason.trim() : (selectedReason ? t(`subscribe.${selectedReason}`) : "");

    if (!reason) {
      setShowReasonError(true);
      return;
    }

    showHideModal(modal_unsubscribe_id);

    try {
      const response = await postCancelSubscription({ reason });
      if (response.data.status === "success") {
        showToast({ type: "success", message: t("subscribe.unsubscribed") });
        setSubscription(response.data);
        setStatus("pending_cancellation");
      } else {
        showToast({ type: "error", message: t("subscribe.error") });
      }
    } catch (error) {
      showToast({ type: "error", message: t("subscribe.error") });
    }
  };

  const checkCoupon = async () => {
    if (!couponCode.trim()) {
      showToast({ type: "error", message: t("please_enter_coupon") });
      return;
    }

    try {
      const response = await postCheckSubscriptionCoupon({ code: couponCode });
      if (response?.data?.success) {
        showToast({
          type: "success",
          message: t("coupon_valid"),
        });
      }
    } catch (error) {
      showToast({
        type: "error",
        message: error?.response?.data?.error || t("invalid_coupon"),
      });
    }
  };

  return (
    <section className="manage-subscriptions py-100">
      <div className="container">
        <div className="subscription-page">
          {status === "subscribed" && (
            <div className="subscription-box subscribed">
              <h2>{t("subscribe.active")}</h2>
              <p>
                <strong>{t("subscribe.note")}</strong>{" "}
                {t("subscribe.stop_warning")}
              </p>
              <ul className="benefits-list">
                {t("subscribe.benefits", { returnObjects: true }).map(
                  (benefit, index) => (
                    <li key={index}>
                      <i className={`bi bi-${benefit.icon} benefit-icon`}></i>{" "}
                      {benefit.text}
                    </li>
                  )
                )}
              </ul>
              <p>
                <i className="bi bi-person-badge"></i> {t("subscribe.plan")}:{" "}
                <strong>
                  {subscription?.plan || t("subscribe.default_plan", { symbol: currency.symbol })}
                </strong>
              </p>
              <button
                className="unsubscribe-btn"
                onClick={showUnsubscribeModal}
              >
                {t("subscribe.unsubscribe")}
              </button>
            </div>
          )}

          {status === "pending_cancellation" && (
            <div className="subscription-box pending-cancellation">
              <h2>{t("subscribe.pending")}</h2>
              <p>{t("subscribe.pending_message")}</p>
              <ul className="benefits-list">
                <li>
                  <i className="bi bi-hourglass-split"></i>{" "}
                  {t("subscribe.revoked")}
                </li>
                <li>
                  <i className="bi bi-journal-check"></i>{" "}
                  {t("subscribe.keep_free_access")}
                </li>
              </ul>
              <p>
                <i className="bi bi-person-badge"></i> {t("subscribe.plan")}:{" "}
                <strong>
                  {subscription?.plan || t("subscribe.default_plan", { symbol: currency.symbol })}
                </strong>
              </p>
            </div>
          )}

          {status === "payment_failed" && (
            <div className="subscription-box payment-failed">
              <h2>{t("subscribe.payment_failed_title")}</h2>
              {subscription?.card_last4 && (
                <p>
                  {t("subscribe.payment_failed_card", {
                    last4: subscription.card_last4,
                    brand: subscription.card_brand || "",
                  })}
                </p>
              )}
              <p>{t("subscribe.payment_failed_message")}</p>
              <button
                className="payment-failed-btn"
                onClick={() => navigate("/update-payment-method")}
              >
                {t("subscribe.update_payment_method")}
              </button>
            </div>
          )}

          {status === "unsubscribed" && (
            <div className="subscription-box not-subscribed">
              <h2>{t("subscribe.offer")}</h2>
              <p>{t("subscribe.price", { symbol: currency.symbol })}</p>
              <p>{t("subscribe.benefits_summary")}</p>
              <ul className="benefits-list">
                {t("subscribe.benefits", { returnObjects: true }).map(
                  (benefit, index) => (
                    <li key={index}>
                      <i className={`bi bi-${benefit.icon} benefit-icon`}></i>{" "}
                      {benefit.text}
                    </li>
                  )
                )}
              </ul>

              {/* Coupon Input */}
              <div className="coupon-input-container">
                <input
                  className="form-control shadow-none"
                  type="text"
                  placeholder={t("enter_your_coupon")}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
                <button
                  onClick={checkCoupon}
                  type="button"
                  className="aai-btn btn-pill-solid d-flex justify-content-center"
                >
                  <i className="fa-solid fa-check"></i>
                </button>
              </div>

              {/* Checkbox for accepting terms */}
              <div className="terms-container">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                />
                <label htmlFor="acceptTerms">
                  {t("subscribe.accept_terms")}{" "}
                  <a
                    href="https://talegacyremembered.com/conditions-generales-de-vente/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("subscribe.terms_link")}
                  </a>
                </label>
              </div>

              <button
                className="subscribe-btn"
                onClick={subscribe}
                disabled={!isChecked}
              >
                {t("subscribe.subscribe")}
              </button>
            </div>
          )}
        </div>
      </div>

      <Modal id={modal_unsubscribe_id}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-8">
              <div className="aai-section-title text-center">
                <p className="cancel-reason-title">
                  {t("subscribe.cancel_reason_title")}
                </p>

                <div className="cancel-reason-list">
                  {CANCELLATION_REASONS.map((reasonKey) => (
                    <button
                      key={reasonKey}
                      className={`cancel-reason-item ${
                        selectedReason === reasonKey ? "selected-item" : ""
                      }`}
                      onClick={() => handleReasonSelect(reasonKey)}
                    >
                      <i
                        className={`fa-regular ${
                          selectedReason === reasonKey
                            ? "fa-circle-check"
                            : "fa-circle"
                        } me-2`}
                      ></i>
                      {t(`subscribe.${reasonKey}`)}
                    </button>
                  ))}

                  <hr className="cancel-reason-divider" />

                  <button
                    className={`cancel-reason-item ${
                      isOtherSelected ? "selected-item" : ""
                    }`}
                    onClick={handleOtherSelect}
                  >
                    <i
                      className={`fa-regular ${
                        isOtherSelected ? "fa-circle-check" : "fa-circle"
                      } me-2`}
                    ></i>
                    {t("subscribe.cancel_reason_other")}
                  </button>

                  {isOtherSelected && (
                    <div className="cancel-reason-custom-input">
                      <input
                        type="text"
                        className={`form-control ${
                          showReasonError && !customReason.trim()
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder={t(
                          "subscribe.cancel_reason_other_placeholder"
                        )}
                        value={customReason}
                        onChange={(e) => {
                          setCustomReason(e.target.value);
                          if (showReasonError) setShowReasonError(false);
                        }}
                        autoFocus
                      />
                    </div>
                  )}
                </div>

                {showReasonError && (
                  <p className="cancel-reason-error">
                    {t("subscribe.cancel_reason_required")}
                  </p>
                )}

                <div className="cancel-reason-buttons mt-3">
                  <button
                    className="cancel-confirm-btn"
                    onClick={confirmUnsubscribe}
                  >
                    {t("subscribe.confirm_unsubscribe")}
                  </button>
                  <button
                    className="cancel-keep-btn"
                    onClick={() => showHideModal(modal_unsubscribe_id)}
                  >
                    {t("subscribe.keep_subscription")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </section>
  );
};

export default ManageSubscriptions;
